import {
  AutocompleteWithLabel,
  InputWithLabel,
  SelectWithLabel
} from '@adotel/components'
import { useQuery, useLazyQuery, useMutation } from '@apollo/client'
import {
  AppBar,
  Button,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import { useEffect, useState, useRef, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import { addJob } from '../../cache/jobs'
import Breadcrumbs from '../../components/Breadcrumb'
import PageHeader from '../../components/PageHeader'
import TimePeriodForm from '../../components/TimePeriodForm'
import { ACCOMMODATIONS_QUERY } from '../../graphql/accommodation'
import { CHANNELS_QUERY } from '../../graphql/channel'
import { INVENTORY_ALLOCATE_AVAILABILITY } from '../../graphql/inventoryAllocateAvailability'
import { ROOMTYPES_QUERY } from '../../graphql/roomtype'
import { TENANTS_QUERY } from '../../graphql/tenant'
import { DateObject, getTodayDate } from '../../helpers/date'
import { getEntityCode, getRoleFromStorage } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { JobsType } from '../../mockData/jobs'
import inventoryPagesRoleManagement from '../../staticRoleManagement/inventoryRoleManagement'
import { Accommodation } from '../../types/accommodation'
import { Channel } from '../../types/channel'
import { AllocateAction, AllocateInput } from '../../types/inventoryAllocate'
import { RoomTypeBrief } from '../../types/roomtype'
import { Tenant } from '../../types/tenants'
import useStyles from './styles/AllocateToChannel.styles'

function AllocateToChannel() {
  const classes = useStyles()
  const history = useHistory()
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  const [selectedTenant, setSelectedTenant] = useState<any>()
  const [
    autocompleteWithLabelAccommodation,
    setautocompleteWithLabelAccommodation
  ] = useState<any>()
  const [selectedRoomType, setSelectedRoomType] = useState<any>()
  const [selectedChannel, setSelectedChannel] = useState<any>()
  const [timePickerSelected, setTimePickerSelected] = useState<boolean>(false)
  const [onDoneClickForCloseDatePicker, setOnDoneClickForCloseDatePicker] =
    useState<boolean>(true)
  const datePickerRef = useRef<HTMLDivElement | null>(null)

  const [dateObject, setdateObject] = useState<DateObject>({})

  const hasAccessToChangeTenant = inventoryPagesRoleManagement
    .find((item) => item.route === '/inventory/:page')
    ?.sections?.SELECT_TENANT.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )
  const hasAccessToChangeAccommodation = inventoryPagesRoleManagement
    .find((item) => item.route === '/inventory/:page')
    ?.sections?.SELECT_ACCOMMODATION.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )
  const [allocateInput, setallocateInput] = useState<AllocateInput>({
    AllocatedNumber: 0,
    AllocationAction: '',
    ChannelCode: '',
    FromDate: '',
    PropertyCode: !hasAccessToChangeAccommodation ? getEntityCode() || '' : '',
    RoomTypeCode: '',
    ToDate: ''
  })
  // tenants query
  const [getTenants, { data: tenantsList, loading: loadingForTenantsList }] =
    useLazyQuery<{
      tenants: Tenant[]
    }>(TENANTS_QUERY)

  useEffect(() => {
    if (hasAccessToChangeTenant) {
      getTenants()
    }
  }, [])

  // accommodations query
  const [
    getAccommodations,
    { data: accommodationsList, loading: loadingForAccommodationsList }
  ] = useLazyQuery<{
    accommodations: Accommodation[]
  }>(ACCOMMODATIONS_QUERY, { notifyOnNetworkStatusChange: true })
  useEffect(() => {
    if (hasAccessToChangeAccommodation) {
      getAccommodations()
    }
  }, [])

  // room types query
  const [
    getRoomTypes,
    { data: roomTypesList, loading: loadingForRoomTypesList }
  ] = useLazyQuery<{
    roomTypes: RoomTypeBrief[]
  }>(ROOMTYPES_QUERY)

  useEffect(() => {
    if (autocompleteWithLabelAccommodation) {
      getRoomTypes({
        variables: {
          propertyCode: autocompleteWithLabelAccommodation.code,
          showDeletedRoomTypes: false,
          showDeactivedRoomTypes: false
        }
      })
    }
    if (!hasAccessToChangeAccommodation) {
      getRoomTypes({
        variables: {
          propertyCode: getEntityCode(),
          showDeletedRoomTypes: false,
          showDeactivedRoomTypes: false
        }
      })
    }
  }, [autocompleteWithLabelAccommodation])

  // channels query
  const { data: channelsList, loading: loadingForChannelsList } = useQuery<{
    channels: Channel[]
  }>(CHANNELS_QUERY)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        datePickerRef.current &&
        !datePickerRef?.current?.contains(event.target as Node) &&
        timePickerSelected
      ) {
        setTimePickerSelected(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [datePickerRef, timePickerSelected])

  const [mutateFunction, { loading }] = useMutation(
    INVENTORY_ALLOCATE_AVAILABILITY,
    {
      onCompleted: (response) => {
        if (response.allocate.trackingID) {
          const newJob: JobsType = {
            trackID: response.allocate.trackingID,
            creationDate: getTodayDate(),
            status: 'InProgress'
          }
          addJob(newJob)
          toast.info(
            getTranslate(
              `allocate job with tracking id ${response.allocate.trackingID} started`
            )
          )
          history.push(
            `/${lang}/allocate-history/${response.allocate.trackingID}/details`
          )
        } else {
          throw new Error(getTranslate('Server error, please try again'))
        }
      },
      onError: (err) => toast.error(err.message)
    }
  )

  const onSave = () => {
    mutateFunction({
      variables: {
        input: allocateInput
      }
    })
  }

  const hasAccessToAllocateAvailability = inventoryPagesRoleManagement
    .find((item) => item.route === '/inventory/:page')
    ?.tabs?.find((tabItem) => tabItem.params.page === 'allocate-availability')
    ?.rolesListWithWriteAccess?.includes(getRoleFromStorage())

  const cachedLevels = useMemo(
    () => [
      {
        label: getTranslate('Allocated Aavailability'),
        link: '/inventory/allocate-availability'
      },
      {
        label: getTranslate('Allocate to channel')
      }
    ],
    []
  )

  const actionOptions: { value: AllocateAction; label: string }[] = [
    { value: 'allocated', label: 'Allocate' },
    { value: 'deallocated', label: 'Deallocate' }
  ]
  return (
    <div className={classes.root}>
      <AppBar className={classes.appBar} position="static" color="transparent">
        <Breadcrumbs levels={cachedLevels} loading={false} />
      </AppBar>
      <div className={classes.contentContainer}>
        <PageHeader
          title={getTranslate('Allocate Room Availability to a Channel')}
        />
        <div className={classes.header}>
          <Typography className={classes.headerTitle} variant="h3" gutterBottom>
            {isSmallScreen
              ? getTranslate('Allocate Availability')
              : getTranslate('Allocate Room Availability to a Channel')}
          </Typography>
        </div>
        <div className={classes.formContainer}>
          <div className={classes.autoCompletesContainer}>
            {hasAccessToChangeTenant && (
              <AutocompleteWithLabel
                value={selectedTenant}
                getOptionLabel={(option) => option.label || ''}
                label={getTranslate('Tenant')}
                inputSize="small"
                placeholder={getTranslate('Select tenant')}
                //TODO:Allocate hardcode remove filter later
                options={
                  tenantsList && !loadingForTenantsList
                    ? tenantsList.tenants
                        .filter((t) =>
                          ['101', '102', '858'].includes(
                            t.basicDetails.displayTenantCode || ''
                          )
                        )
                        .map((item) => ({
                          label: item.basicDetails.name,
                          value: item.basicDetails?.tenantId || '',
                          code: item.basicDetails?.tenantCode || ''
                        }))
                    : []
                }
                onChange={(_, v: any) => {
                  if (v) {
                    setSelectedTenant(v)
                    setautocompleteWithLabelAccommodation(null)
                    setSelectedRoomType(null)
                    setallocateInput({
                      ...allocateInput,
                      PropertyCode: '',
                      RoomTypeCode: ''
                    })
                    getAccommodations({
                      variables: {
                        tenantId: v.value
                      }
                    })
                  } else {
                    setSelectedTenant(null)
                    setautocompleteWithLabelAccommodation(null)
                    setSelectedRoomType(null)
                    setallocateInput({
                      ...allocateInput,
                      PropertyCode: '',
                      RoomTypeCode: ''
                    })
                  }
                }}
                loading={loadingForTenantsList}
                loadingText={getTranslate('Loading') + '...'}
              />
            )}
            {hasAccessToChangeAccommodation && (
              <AutocompleteWithLabel
                value={autocompleteWithLabelAccommodation}
                getOptionLabel={(option) => option.label || ''}
                label={getTranslate('Accommodation')}
                inputSize="small"
                placeholder={getTranslate('Select accommodation')}
                //TODO:Allocate hardcode remove filter later
                options={
                  accommodationsList
                    ? accommodationsList.accommodations.map((item) => ({
                        label: item.basicDetails.name,
                        value: item.basicDetails.accommodationID || '',
                        code: item.basicDetails.propertyCode || ''
                      }))
                    : []
                }
                onChange={(_, v: any) => {
                  if (v) {
                    setautocompleteWithLabelAccommodation(v)
                    setSelectedRoomType(null)
                    setallocateInput({
                      ...allocateInput,
                      PropertyCode: v.code,
                      RoomTypeCode: ''
                    })
                  } else {
                    setautocompleteWithLabelAccommodation(null)
                    setSelectedRoomType(null)
                    setallocateInput({
                      ...allocateInput,
                      PropertyCode: '',
                      RoomTypeCode: ''
                    })
                  }
                }}
                loading={loadingForAccommodationsList}
                loadingText={getTranslate('Loading') + '...'}
              />
            )}
            <AutocompleteWithLabel
              value={selectedRoomType}
              getOptionLabel={(option) => option.label || ''}
              label={getTranslate('Room Type')}
              inputSize="small"
              placeholder={getTranslate('Select Room type')}
              options={
                roomTypesList
                  ? roomTypesList.roomTypes
                      .filter((ele) => ele.type === 'Physical')
                      .map((item) => ({
                        label: item.name,
                        value: item.displayRoomTypeCode || '',
                        code: item.displayRoomTypeCode || ''
                      }))
                  : []
              }
              onChange={(_, v: any) => {
                if (v) {
                  setSelectedRoomType(v)

                  setallocateInput({
                    ...allocateInput,
                    RoomTypeCode: v.code
                  })
                } else {
                  setSelectedRoomType(null)

                  setallocateInput({
                    ...allocateInput,
                    RoomTypeCode: ''
                  })
                }
              }}
              loading={loadingForRoomTypesList}
              loadingText={getTranslate('Loading') + '...'}
            />
            <AutocompleteWithLabel
              value={selectedChannel}
              getOptionLabel={(option) => option.label || ''}
              label={getTranslate('Channel')}
              inputSize="small"
              placeholder={getTranslate('Select Channel')}
              options={
                channelsList?.channels.map((item) => ({
                  label: item.basicDetails.name,
                  value: item.basicDetails.displayChannelCode || ''
                })) || []
              }
              onChange={(_, v: any) => {
                if (v) {
                  setSelectedChannel(v)

                  setallocateInput({
                    ...allocateInput,
                    ChannelCode: v.value
                  })
                } else {
                  setSelectedChannel(null)

                  setallocateInput({
                    ...allocateInput,
                    ChannelCode: ''
                  })
                }
              }}
              loading={loadingForChannelsList}
              loadingText={getTranslate('Loading') + '...'}
            />
            <SelectWithLabel
              fullWidth
              value={allocateInput.AllocationAction}
              onChange={(event) =>
                setallocateInput({
                  ...allocateInput,
                  AllocationAction: event.target.value as AllocateAction
                })
              }
              size="small"
              label={getTranslate('Action')}>
              {actionOptions.map((action) => (
                <MenuItem key={action.value} value={action.value}>
                  {getTranslate(action.label)}
                </MenuItem>
              ))}
            </SelectWithLabel>
            <InputWithLabel
              label={getTranslate('Allocated Number')}
              placeholder={getTranslate('Enter allocated number')}
              type="number"
              pattern="[0-9]*"
              autoComplete="off"
              min={1}
              max={6}
              step={1}
              value={allocateInput.AllocatedNumber}
              inputSize="small"
              onChange={(e) => {
                let value = parseInt(e.currentTarget.value)

                if (isNaN(value) || value < 1) value = 1
                // if (value > 6) value = 6

                // Reject if the value is negative or zero
                if (value > 0) {
                  setallocateInput({
                    ...allocateInput,
                    AllocatedNumber: value
                  })
                } else {
                  setallocateInput({
                    ...allocateInput,
                    AllocatedNumber: 0
                  })
                }
              }}
            />
          </div>
          <div
            className={[
              classes.timePicker,
              isSmallScreen ? classes.timePickerForMobile : ''
            ].join(' ')}
            ref={datePickerRef}>
            <TimePeriodForm
              isOpen={timePickerSelected}
              setTimePickerSelected={setTimePickerSelected}
              dateObject={dateObject}
              onChange={(data: DateObject) => {
                setdateObject(data)
                setallocateInput({
                  ...allocateInput,
                  FromDate: data.gregorianStartDate || '',
                  ToDate: data.gregorianEndDate || ''
                })
              }}
              onDoneClickForCloseDatePicker={onDoneClickForCloseDatePicker}
              setOnDoneClickForCloseDatePicker={
                setOnDoneClickForCloseDatePicker
              }
              endDateCanBeSelectAsStartDate
              // maxRange={5}
            />
          </div>
        </div>
        <div className={classes.footer}>
          <Button
            disabled={
              !hasAccessToAllocateAvailability ||
              loading ||
              !allocateInput.AllocatedNumber ||
              !allocateInput.ToDate
            }
            variant="contained"
            color="primary"
            onClick={onSave}>
            {allocateInput.AllocationAction === 'deallocated'
              ? getTranslate('Deallocate')
              : getTranslate('Allocate')}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default AllocateToChannel
